import type { AuthenticationResult } from '@azure/msal-browser';

/** The B2C ID-token claims that name the policy a token came from: `tfp` on current user flows,
 *  `acr` on legacy ones. MSAL types idTokenClaims as a bare object, hence the local shape. */
type PolicyClaims = { tfp?: string; acr?: string };

/** Policy name behind a B2C authority URL — its last path segment — lower-cased, or null when no
 *  authority is configured (dev/local have no separate reset policy, see environment.dev.ts). B2C
 *  echoes the policy name back in `tfp` with whatever casing it was created with, and MSAL
 *  canonicalises authorities to lower-case with a trailing slash, so compare case-insensitively. */
export function policyNameFromAuthority(authority: string | undefined): string | null {
  if (!authority) return null;
  const segment = authority.split('?')[0].split('/').filter(Boolean).pop();
  return segment ? segment.toLowerCase() : null;
}

/** True when a redirect result was issued by the dedicated password-reset policy rather than the
 *  sign-in one (login.component.ts relaunches MSAL against that policy on AADB2C90118). Decided on
 *  the token's own policy claim first, falling back to the authority MSAL recorded for the request,
 *  so it holds even if the reset policy is configured to emit no policy claim at all. */
export function isPasswordResetPolicyResult(result: AuthenticationResult, resetAuthority: string | undefined): boolean {
  const resetPolicy = policyNameFromAuthority(resetAuthority);
  if (!resetPolicy) return false;
  const claims = (result.idTokenClaims ?? {}) as PolicyClaims;
  const claimed = (claims.tfp || claims.acr || '').toLowerCase();
  if (claimed) return claimed === resetPolicy;
  return policyNameFromAuthority(result.authority) === resetPolicy;
}
